/**
 * Plain-language Governance and Observe pages (ADR-0066): activity (orchestrator), agent
 * overview, quality and health (control plane).
 */
import type { Http } from "./http";
import type { RequestStatus } from "./story";
import type { Classification, RiskTier, Text } from "./types";

const enc = encodeURIComponent;

// ---------------------------------------------------------------- activity (orchestrator)
export type ActivityOutcome = "completed" | "refused" | "failed" | "cancelled" | "waiting" | "running";
export const ACTIVITY_OUTCOMES: ActivityOutcome[] = ["running", "waiting", "completed", "refused", "failed", "cancelled"];

export interface ActivityReason {
  kind: string;
  reason: string;
  node: string | null;
  count: number;
}

/** GET /v1/runs item: one request and how it went. */
export interface ActivityItem {
  run_id: string;
  agent: string;
  agent_ref: string;
  version: string;
  user_id: string | null;
  case_id: string | null;
  /** The start of what was asked (own requests, or roles that read runs). */
  excerpt: string | null;
  outcome: ActivityOutcome;
  status: string;
  /** The shared status words (platform_common.outcomes); older orchestrators leave it out. */
  status_key?: RequestStatus;
  started_at: string;
  ended_at: string | null;
  duration_ms: number | null;
  waiting_at?: string | null;
  waiting_since?: string | null;
  approver_roles?: string[];
  /** A "Wait for" step (ADR-0094): what it waits for (event, answer, return, time) and when it gives up. */
  waiting_for?: string[];
  gives_up_at?: string | null;
  waiting_event?: string | null;
  refusal?: ActivityReason | null;
  failure?: string | null;
}

/** The shared status of an Activity item (its own key, or worked out like the orchestrator). */
export function activityStatus(item: Pick<ActivityItem, "outcome" | "status_key" | "refusal">): RequestStatus {
  if (item.status_key) return item.status_key;
  switch (item.outcome) {
    case "completed":
      return "answered";
    case "refused": {
      const kind = item.refusal?.kind ?? "";
      if (kind.startsWith("guardrail")) return "blocked";
      return kind === "approval_rejected" ? "rejected" : "refused";
    }
    default:
      return item.outcome;
  }
}

export interface ActivityPage {
  scope: "tenant" | "own";
  from: string;
  to: string;
  items: ActivityItem[];
  total: number;
  offset: number;
  limit: number;
  truncated: boolean;
}

export interface ActivityQuery {
  status?: ActivityOutcome[];
  agent?: string;
  user?: string;
  scope?: "auto" | "tenant" | "own";
  from?: string;
  to?: string;
  offset?: number;
  limit?: number;
}

export interface ActivitySummary {
  scope: "tenant" | "own";
  from: string;
  to: string;
  requests: number;
  previous_requests: number;
  outcomes: Partial<Record<ActivityOutcome, number>>;
  per_day: { date: string; requests: number }[];
  agents: { agent: string; requests: number; outcomes: Partial<Record<ActivityOutcome, number>> }[];
  waiting: { count: number; oldest_since: string | null; approver_roles: string[] };
  failures: ActivityReason[];
  refusals: ActivityReason[];
  truncated: boolean;
}

// ---------------------------------------------------------------- agent overview (control plane)
export type AgentStage = "draft" | "submitted" | "dev" | "test" | "staging" | "prod";

/** GET /v1/governance/{tenant}/agents: what people need to judge an agent. */
export interface AgentOverview {
  name: string;
  owner: string;
  status: "active" | "suspended" | "retired" | string;
  agent_id: string;
  risk_tier: RiskTier | null;
  certification: string;
  status_note?: string | null;
  latest_version: string | null;
  latest_state: "draft" | "submitted" | null;
  shown_version: string | null;
  versions: number;
  deployments: Record<string, { version: string; deployed_at: string }>;
  stage: AgentStage;
  description: Text;
  role: Text;
  tools: { ref: string; actions: string[] }[];
  knowledge: string[];
  approvals: { tool: string; action: string; approver_roles: string[] }[];
  human_steps: number;
  model: string;
  channels: string[];
  max_classification: Classification | string;
  autonomous: boolean;
  pattern: string;
}

// ---------------------------------------------------------------- quality (control plane)
export interface GateCheck {
  value: number | null;
  min?: number;
  max?: number;
  passed: boolean;
}

export interface QualityResult {
  suite: string;
  version: string;
  digest: string;
  passed: boolean;
  metrics: Record<string, number>;
  run_ref: string | null;
  checks: Record<string, GateCheck>;
  posted_at: string;
}

export interface QualitySubject {
  kind: "agent" | "model";
  name: string;
  owner: string;
  status: string;
  attributes: { provider?: string; hosting?: string; risk_tier?: number };
  latest_version: string | null;
  latest_digest: string | null;
  deployments: Record<string, string>;
  results: QualityResult[];
}

export interface QualityReport {
  tenant: string;
  /** Distinct sign-offs per target environment; `prod_high_tier` for risk tier 3+ to prod. */
  approvals: Record<string, number>;
  /** kind → environment → suites that must pass before entering it. */
  gates: Record<string, Record<string, string[]>>;
  /** suite → metric → bounds. */
  suites: Record<string, Record<string, { min?: number; max?: number }>>;
  agents: QualitySubject[];
  models: QualitySubject[];
}

// ---------------------------------------------------------------- health (control plane)
export interface HealthPart {
  name: string;
  up: boolean;
  targets: number;
  down: number;
  /** A part the platform works without (the prompt library, local voice): never degrades the status. */
  optional?: boolean;
}

export interface HealthAlert {
  name: string;
  severity: "critical" | "warning" | "info";
  summary: Text;
  /** What the alert is about (service_name, agent, tool, model, queue, …), for plain wording. */
  subject?: Record<string, string>;
  since: string;
}

export interface HealthReport {
  status: "ok" | "degraded" | "down" | "unknown";
  source: "prometheus" | "none";
  checked_at: string;
  parts: HealthPart[];
  alerts: HealthAlert[];
}

/** Query string of GET /v1/runs (`status` repeats). */
export function activityQuery(q: ActivityQuery): string {
  const qs = new URLSearchParams();
  for (const s of q.status ?? []) qs.append("status", s);
  for (const k of ["agent", "user", "scope", "from", "to", "offset", "limit"] as const) {
    const v = q[k];
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export function observeApi(http: Http) {
  return {
    /** Requests of the tenant (operators, tenant admins, auditors) or the caller's own. */
    activity: (q: ActivityQuery = {}) => http.get<ActivityPage>("orchestrator", `/v1/runs${activityQuery(q)}`),
    summary: (q: { agent?: string; scope?: "auto" | "tenant" | "own"; from?: string; to?: string } = {}) =>
      http.get<ActivitySummary>("orchestrator", "/v1/activity/summary", q),
    agents: (tenant: string) => http.get<AgentOverview[]>("controlPlane", `/v1/governance/${enc(tenant)}/agents`),
    quality: (tenant: string) => http.get<QualityReport>("controlPlane", `/v1/quality/${enc(tenant)}`),
    health: () => http.get<HealthReport>("controlPlane", "/v1/ops/health"),
  };
}
